"use client";

import type { Visit } from "@gettactic/api";
import type { PaginatedResult } from "@gettactic/api/src/endpoints/response/paginated_result";
import type { DateRange } from "@gettactic/components/src/calendar/types";
import { useAuthenticated } from "@gettactic/hooks";
import { useInfiniteQuery } from "@tanstack/react-query";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useUrlQuerySync } from "@/features/dashboard/hooks/useUrlQuerySync";
import { api } from "@/lib/api/api";
import { useRouter } from "@/lib/useRouter";
import {
  fromVisitorsUrlQuery,
  toVisitorsUrlQuery
} from "../lib/visitorsUrlQuery";
import { buildVisitsFilterParams } from "./buildVisitsFilterParams";

export type VisitorTab =
  | "log"
  | "upcoming"
  | "preregistered"
  | "visitors"
  | "bulkImports";
export type VisitorStatus = "all" | "checked_in" | "checked_out" | "expected";

export interface VisitorTypeOption {
  id: string;
  name: string;
}

interface VisitorsContextValue {
  activeTab: VisitorTab;
  selectedVisitor: Visit | null;
  setSelectedVisitor: (visitor: Visit | null) => void;
  visitors: Visit[];
  isLoading: boolean;
  searchText: string;
  setSearchText: (text: string) => void;
  statusFilter: VisitorStatus;
  setStatusFilter: (status: VisitorStatus) => void;
  selectedDateRange: DateRange;
  setSelectedDateRange: (range: DateRange) => void;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  selectedOfficeIds: string[];
  setSelectedOfficeIds: (ids: string[]) => void;
  selectedHostId: string;
  setSelectedHostId: (id: string) => void;
  visitorTypeFilter: string;
  setVisitorTypeFilter: (typeId: string) => void;
  availableVisitorTypes: VisitorTypeOption[];
}

const VisitorsContext = createContext<VisitorsContextValue | undefined>(
  undefined
);

export const useVisitorsContext = () => {
  const context = useContext(VisitorsContext);
  if (!context) {
    throw new Error(
      "useVisitorsContext must be used within a VisitorsProvider"
    );
  }
  return context;
};

interface VisitorsProviderProps {
  children: React.ReactNode;
  activeTab: VisitorTab;
  /** Initialize selectedOfficeIds with a single office (for dashboard embed) */
  initialOfficeId?: string;
  /** Skip URL sync effects (for dashboard embed where URL manipulation would conflict) */
  disableUrlSync?: boolean;
  /** Called with the tab a shared or reloaded URL asks for */
  onActiveTabFromUrl?: (tab: VisitorTab) => void;
}

export const VisitorsProvider: React.FC<VisitorsProviderProps> = ({
  children,
  activeTab,
  initialOfficeId,
  disableUrlSync,
  onActiveTabFromUrl
}) => {
  const router = useRouter();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const [selectedVisitor, setSelectedVisitor] = useState<Visit | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<VisitorStatus>("all");
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>(() => ({
    from: new Date(),
    to: new Date()
  }));
  const [selectedOfficeIds, setSelectedOfficeIds] = useState<string[]>(() => {
    if (initialOfficeId) return [initialOfficeId];
    return authenticatedUser.offices?.offices.map((x) => x.id) ?? [];
  });
  const [selectedHostId, setSelectedHostId] = useState<string>("");
  const [visitorTypeFilter, setVisitorTypeFilter] = useState<string>("all");

  // Tab and filters live in the query string, so a reload or a shared link
  // lands on the same screen; defaults stay out of it
  const [urlDefaults] = useState(() => ({
    today: new Date(),
    officeIds: authenticatedUser.offices?.offices.map((x) => x.id) ?? []
  }));
  const urlQuery = useMemo(
    () =>
      toVisitorsUrlQuery(
        {
          activeTab,
          searchText,
          visitorTypeFilter,
          dateRange: selectedDateRange,
          selectedHostId,
          selectedOfficeIds
        },
        urlDefaults
      ),
    [
      activeTab,
      searchText,
      visitorTypeFilter,
      selectedDateRange,
      selectedHostId,
      selectedOfficeIds,
      urlDefaults
    ]
  );
  useUrlQuerySync(
    urlQuery,
    (params) => {
      const fromUrl = fromVisitorsUrlQuery(params);
      if (fromUrl.activeTab) onActiveTabFromUrl?.(fromUrl.activeTab);
      if (fromUrl.searchText) setSearchText(fromUrl.searchText);
      if (fromUrl.visitorTypeFilter) {
        setVisitorTypeFilter(fromUrl.visitorTypeFilter);
      }
      if (fromUrl.dateRange) setSelectedDateRange(fromUrl.dateRange);
      if (fromUrl.selectedHostId) setSelectedHostId(fromUrl.selectedHostId);
      if (fromUrl.selectedOfficeIds) {
        setSelectedOfficeIds(fromUrl.selectedOfficeIds);
      }
    },
    !disableUrlSync,
    false,
    true
  );

  const hasInitializedFromUrl = useRef(false);
  const isUpdatingFromUrl = useRef(false);

  // When initialOfficeId is provided (dashboard embed), use it directly
  // instead of relying on state sync, to avoid stale-state issues on remount.
  const effectiveOfficeIds = initialOfficeId
    ? [initialOfficeId]
    : selectedOfficeIds;

  // Build filter params based on current filters and active tab
  const filterParams = useMemo(
    () =>
      buildVisitsFilterParams({
        activeTab,
        officeIds: effectiveOfficeIds,
        hostId: selectedHostId,
        searchText,
        dateRange: selectedDateRange,
        statusFilter,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        today: new Date()
      }),
    [
      effectiveOfficeIds,
      selectedHostId,
      searchText,
      selectedDateRange,
      statusFilter,
      activeTab
    ]
  );

  // Fetch visits using the new API
  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    status
  } = useInfiniteQuery<PaginatedResult<Visit>>({
    queryKey: ["visits.list", filterParams],
    queryFn: async ({ pageParam: cursor }) => {
      const params = { ...filterParams };
      if (cursor) {
        params.cursor = cursor as string;
      }
      const res = await api.client.visits.list(params);
      return res.result as PaginatedResult<Visit>;
    },
    initialPageParam: undefined,
    enabled: activeTab !== "bulkImports",
    getNextPageParam: (lastPage) =>
      (lastPage?.elements.length ?? 0) < (filterParams.limit ?? 0)
        ? undefined
        : (lastPage?.cursor ?? undefined)
  });

  // Flatten paginated data
  const allVisitors = useMemo(() => {
    return (data?.pages || []).flatMap((page) => page?.elements ?? []);
  }, [data]);

  // Derive available visitor types from fetched visits
  const availableVisitorTypes = useMemo((): VisitorTypeOption[] => {
    const typeMap = new Map<string, string>();
    for (const visit of allVisitors) {
      if (visit.visitor_type) {
        typeMap.set(visit.visitor_type.id, visit.visitor_type.name);
      }
    }
    return Array.from(typeMap, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [allVisitors]);

  // Filter by visitor type (host filtering is server-side via host_id param)
  const filteredVisitors = useMemo(() => {
    if (visitorTypeFilter === "all") return allVisitors;
    return allVisitors.filter((v) => v.visitor_type?.id === visitorTypeFilter);
  }, [allVisitors, visitorTypeFilter]);

  // Read visitor ID from URL on initial load
  useEffect(() => {
    if (disableUrlSync) return;
    if (!router.isReady) return;

    const visitorIdFromUrl = router.query.visitor;
    const visitorId = Array.isArray(visitorIdFromUrl)
      ? visitorIdFromUrl[0]
      : visitorIdFromUrl;

    if (
      visitorId &&
      !hasInitializedFromUrl.current &&
      filteredVisitors.length > 0
    ) {
      const visitor = filteredVisitors.find((v) => v.id === visitorId);
      if (visitor) {
        isUpdatingFromUrl.current = true;
        setSelectedVisitor(visitor);
      }
      hasInitializedFromUrl.current = true;
    }
  }, [disableUrlSync, router.isReady, router.query.visitor, filteredVisitors]);

  // Update URL when selectedVisitor changes
  useEffect(() => {
    if (disableUrlSync) return;
    if (!router.isReady || isUpdatingFromUrl.current) {
      isUpdatingFromUrl.current = false;
      return;
    }

    const currentVisitorId = Array.isArray(router.query.visitor)
      ? router.query.visitor[0]
      : router.query.visitor;

    const newVisitorId = selectedVisitor?.id;

    if (currentVisitorId !== newVisitorId) {
      const newQuery = { ...router.query };
      if (newVisitorId) {
        newQuery.visitor = newVisitorId;
      } else {
        delete newQuery.visitor;
      }

      router.replace(
        {
          pathname: router.pathname,
          query: newQuery
        },
        undefined,
        { shallow: true }
      );
    }
  }, [disableUrlSync, selectedVisitor, router]);

  return (
    <VisitorsContext.Provider
      value={{
        activeTab,
        selectedVisitor,
        setSelectedVisitor,
        visitors: filteredVisitors,
        isLoading: isFetching && status === "pending",
        searchText,
        setSearchText,
        statusFilter,
        setStatusFilter,
        selectedDateRange,
        setSelectedDateRange,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage,
        isFetchingNextPage,
        selectedOfficeIds: effectiveOfficeIds,
        setSelectedOfficeIds,
        selectedHostId,
        setSelectedHostId,
        visitorTypeFilter,
        setVisitorTypeFilter,
        availableVisitorTypes
      }}
    >
      {children}
    </VisitorsContext.Provider>
  );
};
